import pluralizer from 'utils/pluralizer'
import { useAppSelector } from 'redux/hooks'
import { TypeBien } from 'interfaces/API'
import cn from 'classnames'
import Location from 'interfaces/Location'
import Etage from 'interfaces/Etage'
import useToggle from 'hooks/useToggle'
import ButtonIcon from 'components/ButtonIcon'
import Button from 'react-bootstrap/Button'
import { makeSelectSearchCircle } from 'redux/features/map/selectors'
import s from './Synthese.module.scss'
import InputEmpriseAuSol from '../InputEmpriseAuSol'
import InputSurfaceTerrain from '../InputSurfaceTerrain'
import InputSurfaceHabitable from '../InputSurfaceHabitable'

interface Props {
  locations: Location[]
  typeBien: number[] | null
  surfaceHabitable?: number | null
  surfaceHabitableMin?: number | null
  surfaceHabitableMax?: number | null
  surfaceTerrainMin?: number
  surfaceTerrainMax?: number | null
  surfaceAuSolMin?: number | null
  surfaceAuSolMax?: number | null
  etages?: Etage[] | null
  piscine?: number | null
  setSurfaceHabitableMin?: (newValue: number) => void
  setSurfaceHabitableMax?: (newValue: number) => void
  setSurfaceAuSolMin?: (newValue: number) => void
  setSurfaceAuSolMax?: (newValue: number) => void
  setSurfaceTerrainMin?: (newValue: number) => void
  setSurfaceTerrainMax?: (newValue: number) => void
  tooltipSurfaceHabitable?: JSX.Element
  tooltipSurfaceAuSol?: JSX.Element
  tooltipsurfaceTerrain?: JSX.Element
  submitForm: () => void
}

const defaultProps = {
  surfaceHabitable: null,
  surfaceHabitableMin: null,
  surfaceHabitableMax: null,
  surfaceTerrainMin: null,
  surfaceTerrainMax: null,
  surfaceAuSolMin: null,
  surfaceAuSolMax: null,
  etages: null,
  piscine: null,
  setSurfaceHabitableMin: null,
  setSurfaceHabitableMax: null,
  setSurfaceAuSolMin: null,
  setSurfaceAuSolMax: null,
  setSurfaceTerrainMin: null,
  setSurfaceTerrainMax: null,
  tooltipSurfaceHabitable: undefined,
  tooltipSurfaceAuSol: undefined,
  tooltipsurfaceTerrain: undefined,
}

const Synthese = ({
  locations,
  typeBien,
  surfaceHabitable,
  surfaceHabitableMin,
  surfaceHabitableMax,
  surfaceTerrainMin,
  surfaceTerrainMax,
  surfaceAuSolMin,
  surfaceAuSolMax,
  etages,
  piscine,
  setSurfaceAuSolMin,
  setSurfaceAuSolMax,
  setSurfaceTerrainMin,
  setSurfaceTerrainMax,
  setSurfaceHabitableMin,
  setSurfaceHabitableMax,
  tooltipSurfaceHabitable,
  tooltipSurfaceAuSol,
  tooltipsurfaceTerrain,
  submitForm,
}: Props): JSX.Element => {
  const searchCircle = useAppSelector(makeSelectSearchCircle)
  const [showEmpriseSurfaceInput, toggle] = useToggle()

  const firstLine = []
  if (typeBien?.includes(TypeBien.Maison)) {
    firstLine.push(`Maison`)

    if (surfaceHabitable !== null) {
      firstLine.push(`${surfaceHabitable}m² de surface habitable environ`)
    }

    if (surfaceHabitableMin && surfaceHabitableMax) {
      firstLine.push(
        `De ${surfaceHabitableMin}m² à ${surfaceHabitableMax} de surface habitable`
      )
    }
    if (etages) {
      const nbEtage = etages.length
      firstLine.push(`${nbEtage} ${pluralizer('étage', nbEtage)}`)
    }
    if (piscine) {
      switch (piscine) {
        case 0:
          firstLine.push(`Piscine`)
          break
        case 1:
          firstLine.push(`Sans piscine`)
          break
        default:
        // Do nothing when piscine is null
      }
    }
  }
  if (typeBien?.includes(TypeBien.Terrain)) {
    firstLine.push(`Terrain`)
  }
  if (typeBien?.includes(TypeBien.Appartement)) {
    firstLine.push(`Appartement`)
  }
  const handleSubmit = () => {
    if (showEmpriseSurfaceInput) submitForm()
    toggle()
  }

  const showInputSurfaceHabitable =
    surfaceHabitableMin !== undefined &&
    surfaceHabitableMax !== undefined &&
    setSurfaceHabitableMin &&
    setSurfaceHabitableMax

  const showInputEmpriseSol =
    surfaceAuSolMin !== undefined &&
    surfaceAuSolMax !== undefined &&
    setSurfaceAuSolMin &&
    setSurfaceAuSolMax

  const showInputSurfaceTerrain =
    surfaceTerrainMin !== undefined &&
    surfaceTerrainMax !== undefined &&
    setSurfaceTerrainMin &&
    setSurfaceTerrainMax

  return (
    <div className={cn('synthese', s.synthese)}>
      {searchCircle !== undefined && (
        <p className={s.title}>Recherche par rayon</p>
      )}
      {searchCircle === undefined && (
        <p className={cn(s.title, s.nomVille)}>
          {`Sur 
          ${locations
            .map(
              (location) =>
                `${location.nomCommune.toLowerCase()} 
                  (${location.codesPostaux.join(', ')})`
            )
            .join(', ')}`}
        </p>
      )}
      {firstLine.length > 0 && <p>{firstLine.join(', ')}</p>}
      <div className={s.inputGroup}>
        {!showEmpriseSurfaceInput && (
          <>
            {surfaceAuSolMin !== null && surfaceAuSolMax !== null && (
              <p
                className={s.emprise}
              >{`De ${surfaceAuSolMin} à ${surfaceAuSolMax}m² d'emprise au sol`}</p>
            )}
            <p
              className={s.surface}
            >{`De ${surfaceTerrainMin} à ${surfaceTerrainMax}m² de terrain`}</p>
          </>
        )}
        {showEmpriseSurfaceInput && (
          <div className={s.wrapper}>
            {showInputSurfaceHabitable && (
              <InputSurfaceHabitable
                className={s.surfaceHabitable}
                tooltip={tooltipSurfaceHabitable}
                leftInput={{
                  value: surfaceHabitableMin || null,
                  onChange: setSurfaceHabitableMin || (() => null),
                }}
                rightInput={{
                  value: surfaceHabitableMax || null,
                  onChange: setSurfaceHabitableMax || (() => null),
                }}
              />
            )}
            {showInputEmpriseSol && (
              <InputEmpriseAuSol
                className={s.empriseSol}
                tooltip={tooltipSurfaceAuSol}
                leftInput={{
                  value: surfaceAuSolMin || null,
                  onChange: setSurfaceAuSolMin || (() => null),
                }}
                rightInput={{
                  value: surfaceAuSolMax || null,
                  onChange: setSurfaceAuSolMax || (() => null),
                }}
              />
            )}
            {showInputSurfaceTerrain && (
              <InputSurfaceTerrain
                className={s.surfaceTerrain}
                tooltip={tooltipsurfaceTerrain}
                leftInput={{
                  value: surfaceTerrainMin || null,
                  onChange: setSurfaceTerrainMin || (() => null),
                }}
                rightInput={{
                  value: surfaceTerrainMax || null,
                  onChange: setSurfaceTerrainMax || (() => null),
                }}
              />
            )}
          </div>
        )}
        {showEmpriseSurfaceInput ? (
          <Button className={s.button} onClick={handleSubmit}>
            Relancer la recherche
          </Button>
        ) : (
          <ButtonIcon
            className={s.button}
            size="xs"
            icon="edit"
            onClick={() => toggle(true)}
          />
        )}
      </div>
    </div>
  )
}

Synthese.defaultProps = defaultProps
export default Synthese
